import { useCallback, useEffect, useState } from "react";
import { Button, Spinner } from "reactstrap";
import { UsersService } from "../../../globals/services/users.service";
import styles from "../Settings.module.scss";

interface SyncSeminarsProps {
    isLoading: boolean;
    setIsLoading: (isLoading: boolean) => void;
}

interface PreviewSeminar {
    Id: string;
    Name: string;
    HN_Title__c?: string;
    HN_Seminar_Type__c?: string;
    HN_Seminar_Invite_Type__c?: string;
    Seminar_Start_Date__c?: string;
    Seminar_End_Date__c?: string;
}

const formatDate = (date?: string) => {
    if (!date) return '';
    const [year, month, day] = date.split('-');
    return `${day}/${month}/${year.slice(2)}`;
};

const formatDateRange = (start?: string, end?: string) => {
    if (!start && !end) return '';
    return [formatDate(start), formatDate(end)].filter(Boolean).join('–');
};

const SyncSeminars = ({ isLoading, setIsLoading }: SyncSeminarsProps) => {
    const [message, setMessage] = useState('');
    const [isSyncing, setIsSyncing] = useState(false);
    const [seminars, setSeminars] = useState<PreviewSeminar[]>([]);
    const [isPreviewLoading, setIsPreviewLoading] = useState(false);
    const [previewError, setPreviewError] = useState('');

    const loadPreview = useCallback(async () => {
        setIsPreviewLoading(true);
        setPreviewError('');
        try {
            const data = await UsersService().previewSyncSeminars();
            setSeminars(data?.seminars || []);
        } catch (err) {
            setPreviewError("Error loading seminars to sync");
        } finally {
            setIsPreviewLoading(false);
        }
    }, []);

    const syncSeminars = useCallback(async () => {
        setIsLoading(true);
        setIsSyncing(true);
        setMessage('');

        try {
            await UsersService().syncSeminars();
            setMessage("Seminars Synced Successfully");
            loadPreview();
        } catch (err) {
            console.error("Error syncing seminars", err);
            setMessage("Error syncing seminars");
        }
        finally {
            setIsLoading(false);
            setIsSyncing(false);
        }
    }, [setIsLoading, loadPreview]);

    useEffect(() => {
        loadPreview();
    }, [loadPreview]);

    useEffect(() => {
        if (isLoading) setMessage('');
    }, [isLoading]);

    const renderPreview = () => {
        if (isPreviewLoading) return <Spinner size="sm" />;
        if (previewError) return <div className="text-danger">{previewError}</div>;
        if (seminars.length === 0) return <div>No seminars match the current sync criteria</div>;

        return (
            <ul className="list-unstyled mb-0">
                {seminars.map((seminar) => {
                    const details = [
                        seminar.HN_Seminar_Type__c,
                        seminar.HN_Seminar_Invite_Type__c,
                        formatDateRange(seminar.Seminar_Start_Date__c, seminar.Seminar_End_Date__c),
                    ].filter(Boolean).join(' · ');

                    return (
                        <li key={seminar.Id} className="mb-1">
                            <strong>{seminar.HN_Title__c || seminar.Name}</strong>
                            {details && <span className="text-muted ms-2">{details}</span>}
                        </li>
                    );
                })}
            </ul>
        );
    };

    return (
        <div className="mb-2 mt-5">
            <h3>Sync Seminars Module</h3>
            <h6 className="mt-4">
                Will sync{!isPreviewLoading && !previewError ? ` (${seminars.length})` : ''}:
            </h6>
            <div className="mb-3">{renderPreview()}</div>
            <Button
                onClick={syncSeminars}
                className={`${styles.syncButton} btn btn-primary`}
                disabled={isLoading}
            >
                {isSyncing ? 'Syncing...' : 'Sync Seminars'}
            </Button>
            {message && <div className="mt-2">{message}</div>}
        </div>
    );
};

export default SyncSeminars;
